// src/components/job-engine/RecurringFormPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { parseExpression } from "cron-parser";
import {
  Stack, Text, TextField, Dropdown, IDropdownOption, Toggle, PrimaryButton, DefaultButton,
  MessageBar, MessageBarType, Dialog, DialogType, DialogFooter,
} from "@fluentui/react";
import { isAxiosError } from "axios";
import { createRecurring, updateRecurring, deleteRecurring } from "@/api/jobEngine";
import type { RecurringUpsert } from "@/api/jobEngine";
import { useRecurringSchedule } from "./useRecurringSchedule";
import { useJobTypesMeta } from "./useJobTypesMeta";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import CronInput from "./CronInput";
import PayloadEditor from "./PayloadEditor";

const isValidJson = (s: string): boolean => { try { JSON.parse(s); return true; } catch { return false; } };

const schema = Yup.object({
  name: Yup.string().trim().required("Name is required").max(200),
  jobType: Yup.string().required("Job type is required"),
  cron: Yup.string()
    .required("Cron expression is required")
    .test("valid-cron", "Invalid cron expression", (v) => {
      if (!v) return false;
      try { parseExpression(v); return true; } catch { return false; }
    }),
  payload: Yup.string()
    .required("Payload is required")
    .test("valid-json", "Payload must be valid JSON", (v) => (v ? isValidJson(v) : false)),
  enabled: Yup.boolean().required(),
});

interface FormValues {
  name: string;
  jobType: string;
  cron: string;
  payload: string;
  enabled: boolean;
}

const RecurringFormPage: React.FC = () => {
  const { recurringId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(recurringId);
  const existing = useRecurringSchedule(recurringId);
  const meta = useJobTypesMeta();

  const isConnected = useJobEngineConnectionState();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: { name: "", jobType: "", cron: "", payload: "{}", enabled: true },
    validationSchema: schema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setServerError(null);
      setSuccess(null);
      const payload: RecurringUpsert = {
        name: values.name,
        jobType: values.jobType,
        cron: values.cron,
        enabled: values.enabled,
        payload: JSON.parse(values.payload),
      };
      try {
        if (isEdit && recurringId) await updateRecurring(recurringId, payload);
        else await createRecurring(payload);
        setSuccess(isEdit ? "Schedule updated" : "Schedule created");
        window.setTimeout(() => navigate("/job-engine/recurring"), 1500);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 403) {
          setServerError("You don't have permission to save schedules.");
        } else if (isAxiosError(err)) {
          setServerError(err.response?.data?.title ?? "Save failed");
        } else {
          setServerError("An unexpected error occurred.");
        }
      }
    },
  });

  useEffect(() => {
    if (!isEdit || !existing.data) return;
    formik.setValues({
      name: existing.data.name,
      jobType: existing.data.jobType,
      cron: existing.data.cron,
      payload: JSON.stringify(existing.data.payload ?? {}, null, 2),
      enabled: existing.data.enabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  const jobTypeOptions: IDropdownOption[] = (meta.data?.jobTypes ?? []).map((t) => ({ key: t.jobType, text: t.jobType }));

  const onDelete = async () => {
    if (!recurringId) return;
    setServerError(null);
    try {
      await deleteRecurring(recurringId);
      navigate("/job-engine/recurring");
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 403) setServerError("You don't have permission to delete schedules.");
      else if (isAxiosError(err)) setServerError(err.response?.data?.title ?? "Delete failed");
      else setServerError("An unexpected error occurred.");
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 720 } }}>
      <div className="page-header">
        <h2 className="page-title">{isEdit ? "Edit schedule" : "New schedule"}</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>

      {serverError && <MessageBar messageBarType={MessageBarType.error}>{serverError}</MessageBar>}
      {success && <MessageBar messageBarType={MessageBarType.success}>{success}</MessageBar>}

      <form onSubmit={formik.handleSubmit}>
        <Stack tokens={{ childrenGap: 12 }}>
          <TextField
            label="Name"
            value={formik.values.name}
            onChange={(_, v) => formik.setFieldValue("name", v ?? "")}
            onBlur={() => formik.setFieldTouched("name")}
            errorMessage={formik.touched.name ? formik.errors.name : undefined}
            required
          />
          <Dropdown
            label="Job type"
            required
            options={jobTypeOptions}
            selectedKey={formik.values.jobType}
            onChange={(_, o) => formik.setFieldValue("jobType", String(o?.key ?? ""))}
            onBlur={() => formik.setFieldTouched("jobType")}
            errorMessage={formik.touched.jobType ? formik.errors.jobType : undefined}
          />
          <CronInput
            value={formik.values.cron}
            onChange={(v) => formik.setFieldValue("cron", v)}
          />
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>Payload (JSON)</Text>
            <PayloadEditor
              value={formik.values.payload}
              onChange={(v) => formik.setFieldValue("payload", v)}
            />
            {formik.touched.payload && formik.errors.payload && (
              <Text variant="small" styles={{ root: { color: "#d13438" } }}>{formik.errors.payload}</Text>
            )}
          </Stack>
          <Toggle
            label="Enabled"
            checked={formik.values.enabled}
            onChange={(_, v) => formik.setFieldValue("enabled", Boolean(v))}
          />

          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton text="Save" type="submit" disabled={!formik.isValid || formik.isSubmitting} />
            <DefaultButton text="Cancel" onClick={() => navigate("/job-engine/recurring")} />
            {isEdit && <DefaultButton text="Delete" onClick={() => setConfirmDelete(true)} />}
          </Stack>
        </Stack>
      </form>

      <Dialog
        hidden={!confirmDelete}
        onDismiss={() => setConfirmDelete(false)}
        dialogContentProps={{ type: DialogType.normal, title: "Delete schedule?", subText: "This cannot be undone." }}
      >
        <DialogFooter>
          <PrimaryButton text="Delete" onClick={onDelete} />
          <DefaultButton text="Cancel" onClick={() => setConfirmDelete(false)} />
        </DialogFooter>
      </Dialog>
    </Stack>
    </div>
  );
};

export default RecurringFormPage;
